import {
	callGetterFor,
	getResult,
	isSmallerOrEqualThan,
} from "../../helpers/functional-util.js";
import { groupBy } from "@foretagsplatsen/core-helpers/array";
import AbstractStream from "./AbstractStream.js";

/**
 * Stream based on an array to provide items.
 *
 * @param {Array|Promise<Array>|function():Array} [data] - Array of
 *   items to stream. It can also be a Promise resolving to such an
 *   array, or a function returning an array
 */
export default class ArrayStream extends AbstractStream {
	constructor({ data = [] } = {}) {
		super(...arguments);

		this._data = data;
	}

	async fetch({
		searchingOptions = {},
		sortingOptions = {},
		groupingOptions = {},
		paginatingOptions = {},
	} = {}) {
		let items = await Promise.resolve(getResult(this._data));

		items = [...items];

		items = this._applySearch({ items, searchingOptions });
		items = this._applySort({ items, sortingOptions });
		items = this._applyGroupBy({ items, groupingOptions });

		const unpaginatedSize = items.length;

		items = this._applyPagination({ items, paginatingOptions });

		return { items, unpaginatedSize };
	}

	_applySearch({ items, searchingOptions: { searchString } }) {
		if (!searchString) return items;

		return items.filter((each) => each.match?.(searchString));
	}

	_applySort({ items, sortingOptions: { propertyName, isAscendingOrder } }) {
		if (!propertyName) return items;

		const baseIndex = isAscendingOrder ? -1 : 1;

		items.sort((one, another) => {
			const oneProperty = callGetterFor(one, propertyName);
			const anotherProperty = callGetterFor(another, propertyName);
			return isSmallerOrEqualThan(oneProperty, anotherProperty)
				? baseIndex
				: -baseIndex;
		});

		return items;
	}

	_applyGroupBy({ items, groupingOptions: { propertyName } }) {
		if (!propertyName) return items;

		return groupBy({
			array: items,
			getKey: (item) => callGetterFor(item, propertyName),
		});
	}

	_applyPagination({
		items,
		paginatingOptions: { numberOfItemsToSkip, numberOfItemsToFetch },
	}) {
		if (!numberOfItemsToFetch) return items;

		return items.slice(
			numberOfItemsToSkip,
			numberOfItemsToSkip + numberOfItemsToFetch,
		);
	}
}
